/**
 * Validatie-helpers voor zakelijke registratie (KvK + btw-nummer). Puur formaat/normalisatie —
 * de échte "bestaat dit bedrijf?"-check gebeurt server-side tegen VIES (EU-btw-register).
 *
 * KvK-nummer: 8 cijfers (sinds 2008 landelijk 8-cijferig). Er bestaat GEEN openbaar
 * check-cijfer-algoritme voor KvK-nummers, dus we controleren alleen het formaat.
 *
 * Btw-nummer: EU-formaat `<landcode><nummer>`. NL is `NL` + 9 cijfers + `B` + 2 cijfers
 * (bv. NL123456789B01). Voor VIES splitsen we landcode + rest.
 */

/** Alleen cijfers overhouden (spaties/punten/leestekens eruit). */
export function normalizeKvk(input: string): string {
  return (input || "").replace(/\D/g, "");
}

/** KvK = exact 8 cijfers. (Geen checksum: KvK publiceert er geen.) */
export function isValidKvkFormat(input: string): boolean {
  return /^\d{8}$/.test(normalizeKvk(input));
}

/**
 * Bedrijfsnummer-formaat afhankelijk van het land van het btw-nummer:
 *  - NL → KvK-nummer, 8 cijfers.
 *  - BE → ondernemingsnummer (KBO/BCE), 10 cijfers (gelijk aan de btw-cijfers).
 *  - onbekend → 8 (NL) óf 10 (BE) cijfers toestaan.
 */
export function isValidCompanyNumberFormat(input: string, country?: string): boolean {
  const d = normalizeKvk(input);
  if (country === "BE") return /^\d{10}$/.test(d);
  if (country === "NL") return /^\d{8}$/.test(d);
  return /^\d{8}$/.test(d) || /^\d{10}$/.test(d);
}

/** Label voor het bedrijfsnummer-veld per land (voor de UI). */
export function companyNumberLabel(country?: string): string {
  return country === "BE" ? "Ondernemingsnummer" : "KvK-nummer";
}

/** Btw-nummer normaliseren: hoofdletters, geen spaties/punten/streepjes. */
export function normalizeVat(input: string): string {
  return (input || "").toUpperCase().replace(/[\s.\-]/g, "");
}

/** Btw-nummer opsplitsen in landcode (2 letters) + nummer. `null` als het formaat niet klopt. */
export function parseVat(input: string): { country: string; number: string } | null {
  const v = normalizeVat(input);
  const m = /^([A-Z]{2})([0-9A-Z]{2,12})$/.exec(v);
  if (!m) return null;
  return { country: m[1]!, number: m[2]! };
}

/** Klopt het btw-nummer qua formaat? NL/BE streng, overige EU-landen globaal. */
export function isValidVatFormat(input: string): boolean {
  const p = parseVat(input);
  if (!p) return false;
  if (p.country === "NL") return /^\d{9}B\d{2}$/.test(p.number); // NL#########B##
  if (p.country === "BE") return /^\d{9,10}$/.test(p.number);    // BE0#########  (9 of 10 cijfers)
  // Overige EU-landen: alleen een globale lengtecheck; VIES doet de echte validatie.
  return p.number.length >= 2 && p.number.length <= 12;
}

/** Landcode (2 letters) uit een btw-nummer, of undefined als het formaat niet klopt. */
export function vatCountry(input: string): string | undefined {
  return parseVat(input)?.country;
}

/** EU-landcodes die VIES ondersteunt (incl. `EL` voor Griekenland, `XI` voor Noord-Ierland). */
export const VIES_COUNTRIES = new Set([
  "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "EL", "ES", "FI", "FR", "HR", "HU", "IE",
  "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK", "XI",
]);

/** Is dit btw-nummer bruikbaar voor een VIES-check (geldig formaat + ondersteund land)? */
export function isViesCheckable(input: string): boolean {
  const p = parseVat(input);
  return !!p && VIES_COUNTRIES.has(p.country) && isValidVatFormat(input);
}
